import { useEffect, type ReactNode } from 'react';
import type { EventModuleKey } from '@conference/shared';
import { useNavigate } from 'react-router';
import { useEventModules } from '../hooks/useEventModules';
import { notify } from '../utils/notifications';

const UNAVAILABLE_MESSAGE = 'This feature is not available for this event.';

function ModuleRedirect({ to, message }: { to: string; message: string }) {
  const navigate = useNavigate();

  useEffect(() => {
    notify.info(message);
    navigate(to, { replace: true });
  }, [message, navigate, to]);

  return <div role="status" className="sr-only">{message}</div>;
}

export default function ModuleRouteGuard({
  module,
  children,
}: {
  module: EventModuleKey | readonly EventModuleKey[];
  children: ReactNode;
}) {
  const { isEnabled, fallbackRoute, isReady, hasError } = useEventModules();

  if (!isReady) {
    return (
      <div role="status" className="size-full flex items-center justify-center">
        Loading event settings…
      </div>
    );
  }

  if (hasError) {
    return (
      <ModuleRedirect
        to="/my-conferences"
        message="Could not load event settings. Please select the event again."
      />
    );
  }

  const required = Array.isArray(module) ? module : [module];
  if (!required.some((key) => isEnabled(key))) {
    return <ModuleRedirect to={fallbackRoute} message={UNAVAILABLE_MESSAGE} />;
  }

  return <>{children}</>;
}
